import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

export interface InfoRow {
  label: string
  value: ReactNode
}

export interface ConfirmationModalProps {
  open: boolean
  title: string
  description: string | ReactNode
  confirmText?: string
  cancelText?: string
  variant?: 'default' | 'danger' | 'warning'
  isLoading?: boolean
  onConfirm: () => void
  onCancel: () => void
  sections?: string[]
  warningText?: string
  headerIcon?: ReactNode
  subtitle?: string | ReactNode
  showCloseButton?: boolean
  infoRows?: InfoRow[]
  confirmIcon?: ReactNode
  footerLayout?: 'grid' | 'flex-right'
  maxWidth?: string
}

export function ConfirmationModal({
  open,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  isLoading = false,
  onConfirm,
  onCancel,
  sections,
  warningText,
  headerIcon,
  subtitle,
  showCloseButton = false,
  infoRows,
  confirmIcon,
  footerLayout = 'grid',
  maxWidth = 'max-w-[440px]',
}: ConfirmationModalProps) {
  useEffect(() => {
    if (!open) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onCancel])

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  if (!open) {
    return null
  }

  const getConfirmButtonClasses = () => {
    const baseClasses =
      'inline-flex h-10 sm:h-11 items-center justify-center gap-1.5 rounded-[10px] px-4 text-xs sm:text-sm font-semibold text-white transition cursor-pointer'
    switch (variant) {
      case 'danger':
        return `${baseClasses} bg-rose-600 hover:bg-rose-700`
      case 'warning':
        return `${baseClasses} bg-amber-600 hover:bg-amber-700`
      case 'default':
      default:
        return `${baseClasses} bg-[#146971] hover:bg-[#0f565d]`
    }
  }

  const hasRichHeader = Boolean(headerIcon || subtitle || showCloseButton || infoRows || footerLayout === 'flex-right')

  const modal = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-[5px]"
      onMouseDown={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmation-modal-title"
        aria-describedby="confirmation-modal-description"
        className={`w-full ${maxWidth} rounded-[20px] bg-white p-5 sm:p-6 shadow-[0_25px_70px_rgba(15,23,42,0.22)] border border-slate-200/80 font-sans ${
          hasRichHeader ? 'text-left' : 'text-center'
        }`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {/* Top Header Row with Optional Icon and Close Button */}
        <div className="flex items-start justify-between gap-3 min-w-0 mb-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            {headerIcon ? (
              <div className="shrink-0 mt-0.5">{headerIcon}</div>
            ) : null}
            <div className="min-w-0 flex-1">
              <h2
                id="confirmation-modal-title"
                className="text-base sm:text-lg font-bold tracking-tight text-slate-900 truncate"
              >
                {title}
              </h2>
              {subtitle ? (
                <p className="text-xs font-medium text-slate-500 mt-0.5 leading-tight">
                  {subtitle}
                </p>
              ) : null}
            </div>
          </div>

          {showCloseButton ? (
            <button
              type="button"
              onClick={onCancel}
              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer shrink-0"
              aria-label="Close modal"
            >
              <X className="w-4 h-4" />
            </button>
          ) : null}
        </div>

        {/* Body Description */}
        {description ? (
          <div
            id="confirmation-modal-description"
            className="text-xs sm:text-sm text-slate-600 leading-relaxed font-normal"
          >
            {description}
          </div>
        ) : null}

        {/* Info Rows Box */}
        {infoRows && infoRows.length > 0 && (
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 space-y-2 text-xs font-sans">
            {infoRows.map((row, idx) => (
              <div key={idx} className="flex items-center justify-between gap-3 min-w-0">
                <span className="text-slate-500 font-medium shrink-0">{row.label}</span>
                <span className="text-slate-900 font-bold truncate text-right">{row.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Optional Sections Chips */}
        {sections && sections.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            {sections.map((sec) => (
              <span key={sec} className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-xs font-semibold">
                Section {sec}
              </span>
            ))}
          </div>
        )}

        {/* Warning Text */}
        {warningText && (
          <div className="mt-4 rounded-lg bg-rose-50 p-3 text-left flex items-start gap-2 border border-rose-100">
            <span className="text-rose-500 shrink-0 text-sm mt-0.5">ℹ</span>
            <p className="text-xs text-rose-600 leading-normal">{warningText}</p>
          </div>
        )}

        {/* Divider Line & Footer Buttons */}
        {footerLayout === 'flex-right' ? (
          <>
            <div className="border-t border-slate-200/90 my-4" />
            <div className="flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={onCancel}
                disabled={isLoading}
                className="px-4 py-2 text-xs sm:text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50"
              >
                {cancelText}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={isLoading}
                className={`${getConfirmButtonClasses()} disabled:opacity-50`}
              >
                {confirmIcon}
                <span>{isLoading ? 'Processing...' : confirmText}</span>
              </button>
            </div>
          </>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="inline-flex h-10 sm:h-11 items-center justify-center rounded-[10px] border border-slate-300 bg-white px-4 text-xs sm:text-sm font-semibold text-slate-700 transition hover:bg-slate-50 cursor-pointer disabled:opacity-50"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className={`${getConfirmButtonClasses()} disabled:opacity-50`}
            >
              {confirmIcon}
              <span>{isLoading ? 'Processing...' : confirmText}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )

  return typeof document === 'undefined' ? modal : createPortal(modal, document.body)
}

export default ConfirmationModal
